/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';
import {
  Filter,
  Notification as UINotification,
  Notifications,
} from '@scope/scope-one-ui/core/Notifications/Notifications.d';
import { useToasts } from '@scope/scope-one-ui/core/Messages/context';
import { useSelector } from 'react-redux';
import { WS_API_URL, getScopeOneUrl } from 'utils';
import { fetchApi } from 'shared/functions';
import {
  LegacyNotification as ApiNotification,
  NotificationCategory,
  Unread,
  DownloadResponse,
} from '../../types/notification';
import { getAuthDataToken } from '../../../selectors/auth';
import {
  fetchUserNotificationsWhatsNew,
  notificationsLimit,
  updateNotification,
  useFetchNotifications,
} from '../../../modules/notificationsCentre/services/notificationsService';
import { useSnackbar } from '../useSnackbar';

export const useGetNotifications = (): Notifications => {
  const token = useSelector(getAuthDataToken);
  const { info: toastInfo } = useToasts();
  const [filter, setFilter] = useState<Filter>({ tab: 'news', type: 'all' });
  const [unread, setUnread] = useState<Unread>({
    totalUnread: 0,
    userUnread: 0,
    whatsNewUnread: 0,
  });
  const { info, success } = useSnackbar();
  const [hasMoreData, setHasMoreData] = useState(true);
  const [list, setList] = useState<UINotification[]>([]);
  const [page, setPage] = useState(1);
  const getNotifications = useFetchNotifications();
  // @ts-ignore
  const whatsNew: ApiNotification[] = fetchUserNotificationsWhatsNew().map((item) => {
    // @ts-ignore
    return { type: item.type.category as NotificationCategory, ...item };
  });

  const downloadDoc = (url: string) => async () => {
    /* Replace origin with current environment */
    const docUrl = `/${url.split('/').slice(4).join('/')}`;

    fetchApi(docUrl)
      .then((res) => res.json())
      .then(({ fileUrl }: DownloadResponse) => {
        window.location.href = fileUrl!;
      })
      .catch((error) => {
        success('Download request has been sent.');
      });
  };

  const parseNotification = (item: ApiNotification): UINotification => ({
    id: item._id,
    headline: item.headline,
    text: item.text,
    date: item.date,
    category: item.type.category,
    isFavourite: item.is_favourite,
    isRead: item.is_read,
    imgUrl: item.img_url,
    ...(item.type.category === 'downloads' && item.type.tag === 'pdf-report-downloads'
      ? { onClick: downloadDoc(item.view_link!) }
      : { viewLink: item.view_link }),
  });

  const connect = () => {
    let mounted = true;
    const ws = new WebSocket(`${WS_API_URL}/ws`, ['Authorization', token]);

    ws.onmessage = (res) => {
      const { data } = JSON.parse(res.data);
      setUnread(data.unread);
      if (data.websocketData?.type?.tag === 'portfolio-upload') {
        toastInfo({
          title: 'Portfolio uploaded',
          content: `Your portfolio is ready to be downloaded`,
        });
      } else if (data.websocketData.headline) {
        info(data.websocketData.headline);
      }
    };

    /* Reconnect only if the connection dropped, but the component is still mounted */
    ws.onclose = () => {
      if (!mounted) return;
      setTimeout(connect, 1000);
    };

    return () => {
      mounted = false;
      ws.close();
    };
  };

  const resetList = () => {
    setPage(1);
    setList([]);
    setHasMoreData(true);
  };

  const handleFilter = (value: Filter) => {
    resetList();
    setFilter(value);
  };

  const loadNews = (_pageNumber: number, mode: 'add' | 'append') => {
    setTimeout(() => {
      setHasMoreData(false);

      const newData = filter.type === 'favourites' ? [] : whatsNew.map(parseNotification);

      if (mode === 'add') {
        setList((prevList) => [...prevList, ...newData]);
      }

      if (mode === 'append') {
        setList(newData);
      }
    }, 300);
  };

  const loadMine = (pageNumber: number, mode: 'add' | 'append') => {
    getNotifications(pageNumber, filter).then((res) => {
      if (res.length < notificationsLimit) {
        setHasMoreData(false);
      }

      const newData = res.map(parseNotification);

      if (mode === 'add') {
        setList((prevList) => [...prevList, ...newData]);
      }

      if (mode === 'append') {
        setList(newData);
      }
    });
  };

  const loadMore = () => {
    if (!hasMoreData) {
      return;
    }

    const newPage = page + 1;

    if (filter.tab === 'news') {
      loadNews(newPage, 'add');
    }

    if (filter.tab === 'mine') {
      loadMine(newPage, 'add');
    }

    setPage(newPage);
  };

  const toggleFav = (id: string, isFav: any) => {
    resetList();
    updateNotification({ id, data: { is_favourite: isFav } })
      .then(() => {
        success(
          isFav ? 'Added notification to favourites.' : 'Removed notification from favourites.',
        );
      })
      .finally(() => {
        loadMine(1, 'append');
      });
  };

  const allowToToggleFav = () => !list.find((item) => item.category === 'whats_new');

  /* Initial websocket connection to retrieve notifications data */
  useEffect(() => connect(), []);

  /* Refetch notifications data on filter change */
  useEffect(() => {
    if (filter.tab === 'news') {
      loadNews(1, 'append');
    }

    if (filter.tab === 'mine') {
      loadMine(1, 'append');
    }
  }, [filter]);

  return {
    unread,
    settingsLink: getScopeOneUrl('news-centre#notifications-settings'),
    hasMoreData,
    list,
    onFilter: handleFilter,
    onLoadMore: loadMore,
    onFavToggle: allowToToggleFav() ? toggleFav : undefined,
  };
};
